import { Injectable, Injector } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { portalRequestParts, PortalRequestParts } from './portal-http-activity.describe';

/** Delay before debug-mode loaders may name in-flight API calls. */
export const PORTAL_LOAD_SLOW_MS = 2000;
const MAX_DETAIL_ITEMS = 4;

interface TrackedRequest {
  id: number;
  startedAt: number;
  method: string;
  parts: PortalRequestParts;
}

@Injectable({ providedIn: 'root' })
export class PortalHttpActivityService {
  readonly changes$ = new Subject<void>();

  private nextId = 1;
  private readonly inflight = new Map<number, TrackedRequest>();

  /**
   * TranslateService loads its JSON through HttpClient. Injecting it here would
   * cycle: TranslateService → HttpClient → this interceptor → TranslateService.
   * Resolve it only when a loader asks for detail, after that construction has finished.
   */
  constructor(private injector: Injector) {}

  private translations(): TranslateService | null {
    try {
      return this.injector.get(TranslateService);
    } catch {
      return null;
    }
  }

  begin(req: HttpRequest<unknown>): number | null {
    const parts = portalRequestParts(req.url);
    if (!parts) {
      return null;
    }
    const id = this.nextId++;
    this.inflight.set(id, {
      id,
      startedAt: Date.now(),
      method: (req.method || 'GET').toUpperCase(),
      parts,
    });
    this.changes$.next();
    return id;
  }

  end(id: number | null): void {
    if (id == null || !this.inflight.delete(id)) {
      return;
    }
    this.changes$.next();
  }

  /** Names of API calls that started while this loader has been visible. */
  detailSince(visibleSince: number): string {
    const graceMs = 400;
    const seen = new Set<string>();
    const labels: string[] = [];
    const rows = [...this.inflight.values()]
      .filter((row) => row.startedAt >= visibleSince - graceMs)
      .sort((a, b) => a.startedAt - b.startedAt);

    for (const row of rows) {
      const label = this.format(row);
      if (!label || seen.has(label)) {
        continue;
      }
      seen.add(label);
      labels.push(label);
    }

    if (!labels.length) {
      return '';
    }
    const items = labels.slice(0, MAX_DETAIL_ITEMS);
    const extra = labels.length - items.length;
    const translate = this.translations();
    const more = translate
      ? translate.instant('COMMON.LOADER.MORE', { count: extra })
      : `+${extra}`;
    const list = extra > 0 ? `${items.join(', ')} ${more}` : items.join(', ');
    return translate
      ? translate.instant('COMMON.LOADER.DETAIL', { items: list })
      : list;
  }

  private format(row: TrackedRequest): string {
    const name = this.lookup(`COMMON.LOADER.RESOURCES.${row.parts.resource}`, humanize(row.parts.resource));
    const action = row.parts.action
      ? this.lookup(`COMMON.LOADER.ACTIONS.${row.parts.action}`, humanize(row.parts.action))
      : '';
    const subject = action ? `${name} (${action})` : name;
    if (row.method === 'GET' || row.method === 'HEAD') {
      return subject;
    }
    const verb = this.lookup(`COMMON.LOADER.VERBS.${row.method}`, row.method);
    return `${verb}: ${subject}`;
  }

  private lookup(key: string, fallback: string): string {
    const value = this.translations()?.instant(key);
    return value && value !== key ? value : fallback;
  }
}

function humanize(segment: string): string {
  const words = segment.replace(/[-_]/g, ' ').trim();
  if (!words) {
    return segment;
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}
